import * as React from "react";
import { useQuery, useMutation } from "@apollo/client";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import Button from "@mui/material/Button";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import CircularProgress from "@mui/material/CircularProgress";
import TableHead from "@mui/material/TableHead";
import Typography from "@mui/material/Typography";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";

import { columns, emptyVan } from "./common";
import CommonDialog from "../common-dialogs/DeleteDialog";
import VanTableRow from "./VanTableRow";
import VanEditableTableRow from "./VanEditableTableRow";
import { FREE_DRIVERS } from "../graphql/users";
import { GET_VANS, CREATE_VAN, UPDATE_VAN, DELETE_VAN } from "../graphql/vans";

export default function VansPage() {
	const { loading, error, data, refetch } = useQuery(GET_VANS);
	const {
		loading: freeDriversLoading,
		error: freeDriversError,
		data: freeDriversData,
		refetch: freeDriversRefetch,
	} = useQuery(FREE_DRIVERS);
	const [
		createVan,
		{ loading: createVanLoading, error: createVanError, data: createVanData },
	] = useMutation(CREATE_VAN);
	const [
		updateVan,
		{ loading: updateVanLoading, error: updateVanError, data: updateVanData },
	] = useMutation(UPDATE_VAN);
	const [
		deleteVan,
		{ loading: deleteVanLoading, error: deletevanError, data: deleteVanData },
	] = useMutation(DELETE_VAN);

	const [addingNewVan, setAddingNewVan] = React.useState(false);
	const [editableRowId, setEditableRowId] = React.useState(null);
	const isEditableRow = (id) => {
		return editableRowId === id;
	};

	const startEditingRow = (id) => {
		if (!id || editableRowId) return;
		setEditableRowId(() => id);
	};
	const cancelEditingRow = (id) => {
		if (id !== editableRowId) return;
		if (id === emptyVan.id) setAddingNewVan(false);
		setEditableRowId(null);
	};
	const saveRow = async (data) => {
		if (editableRowId !== data.id) return;
		if (editableRowId === emptyVan.id) {
			await createVan({
				variables: {
					input: { ...data, id: undefined },
				},
			});
		} else {
			await updateVan({
				variables: {
					input: { ...data },
				},
			});
		}
		await refetch();
		await freeDriversRefetch();
		setEditableRowId(null);
		if (data.id === emptyVan.id) {
			setAddingNewVan(false);
		}
	};
	const [deleteRowId, setDeleteRowId] = React.useState(null);
	const tryDeleteRow = (id) => {
		if (deleteRowId) return;
		setDeleteRowId(id);
	};
	const deleteRow = async () => {
		await deleteVan({
			variables: {
				id: deleteRowId,
			},
		});
		setDeleteRowId(null);
		setEditableRowId(null);
		await refetch();
	};

	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(25);

	const isLoading = () =>
		loading ||
		updateVanLoading ||
		createVanLoading ||
		deleteVanLoading ||
		freeDriversLoading;

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(+event.target.value);
		setPage(0);
	};
	return (
		<>
			{!isLoading() && (
				<div>
					<Typography
						variant="h4"
						align="center"
						component="div"
						href="/"
						sx={{
							fontFamily: "monospace",
							fontWeight: 700,
							margin: "auto",
						}}
					>
						Список автомобілів
					</Typography>
					<div
						style={{
							display: "flex",
							marginRight: 10,
							justifyContent: "flex-end",
						}}
					>
						<div>
							<Button
								onClick={() => {
									setAddingNewVan((oldState) => !oldState);
									setEditableRowId(emptyVan.id);
								}}
								color={addingNewVan ? "secondary" : "primary"}
								variant="contained"
							>
								Створити новий
							</Button>
						</div>
					</div>
					<Paper sx={{ width: "100%", overflow: "hidden" }}>
						<TableContainer sx={{}}>
							<Table stickyHeader aria-label="sticky table">
								<TableHead>
									<TableRow>
										{columns.map((column) => (
											<TableCell
												key={column.id}
												align={column.align}
												style={{ minWidth: column.minWidth }}
											>
												{column.label}
											</TableCell>
										))}
									</TableRow>
								</TableHead>
								<TableBody>
									{addingNewVan && (
										<VanEditableTableRow
											key={emptyVan.id}
											row={emptyVan}
											drivers={freeDriversData.freeDrivers}
											actions={{
												saveRow: saveRow,
												cancelEdit: cancelEditingRow,
											}}
										/>
									)}
									{data.cars
										.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
										.map((row) => {
											return isEditableRow(row.id) ? (
												<VanEditableTableRow
													key={row.id}
													row={row}
													drivers={freeDriversData.freeDrivers}
													actions={{
														saveRow: saveRow,
														tryDeleteRow: tryDeleteRow,
														cancelEdit: cancelEditingRow,
													}}
												/>
											) : (
												<VanTableRow
													key={row.id}
													row={row}
													buttonsActive={!editableRowId}
													actions={{
														editRow: startEditingRow,
														tryDeleteRow: tryDeleteRow,
													}}
												/>
											);
										})}
								</TableBody>
							</Table>
						</TableContainer>
						<TablePagination
							rowsPerPageOptions={[10, 25, 100]}
							component="div"
							count={data.cars.length}
							rowsPerPage={rowsPerPage}
							page={page}
							onPageChange={handleChangePage}
							onRowsPerPageChange={handleChangeRowsPerPage}
						/>
					</Paper>
				</div>
			)}
			{isLoading() && (
				<CircularProgress
					size={24}
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
					}}
				/>
			)}
			<CommonDialog
				open={!!deleteRowId}
				question={`Ви дійсно хочете видалити автомобіль?`}
				cancelAction={() => setDeleteRowId(null)}
				confirmAction={deleteRow}
			/>
		</>
	);
}
